import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import './Staff.css';

function Staff() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reservations, setReservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(
        `/staff/reservations?password=${encodeURIComponent(password)}&date=${selectedDate}`
      );
      setReservations(resp.data.reservations);
      setTotal(resp.data.total);
      setFetchError('');
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthenticated(false);
        setLoginError('Session expired. Please log in again.');
      } else {
        setFetchError('Failed to load reservations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [password, selectedDate]);

  useEffect(() => {
    if (authenticated) {
      fetchReservations();
    }
  }, [authenticated, selectedDate, fetchReservations]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      await api.post('/staff/login', { password });
      setAuthenticated(true);
    } catch (err) {
      setLoginError('Invalid password. Please try again.');
    }
  };

  const formatTime = (isoString) => {
    const [, timePart] = isoString.split('T');
    const [hourStr, minStr] = timePart.split(':');
    const hour = parseInt(hourStr, 10);
    const min = minStr.padStart(2, '0');
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min} ${period}`;
  };

  if (!authenticated) {
    return (
      <div className="staff-page">
        <div className="page-header">
          <h1>Staff Access</h1>
          <div className="section-divider"></div>
        </div>
        <section className="section">
          <div className="container">
            <div className="staff-login">
              <h2>Enter Staff Password</h2>
              <form onSubmit={handleLogin} className="staff-login__form">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="staff-login__input"
                  required
                />
                <button type="submit" className="btn-primary">Login</button>
              </form>
              {loginError && <p className="staff-login__error">{loginError}</p>}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="staff-page">
      <div className="page-header">
        <h1>Reservation List</h1>
        <div className="section-divider"></div>
      </div>

      <section className="section">
        <div className="container">
          <div className="staff-controls">
            <div className="staff-controls__date">
              <label htmlFor="staff-date">Date:</label>
              <input
                type="date"
                id="staff-date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <div className="staff-controls__summary">
              <span className="staff-controls__total">{total} reservation{total !== 1 ? 's' : ''}</span>
              <button onClick={() => { setAuthenticated(false); setPassword(''); }} className="staff-controls__logout">
                Logout
              </button>
            </div>
          </div>

          {fetchError && <p className="staff-fetch-error">{fetchError}</p>}

          {loading ? (
            <p className="staff-loading">Loading reservations...</p>
          ) : reservations.length === 0 ? (
            <div className="staff-empty">
              <p>No reservations for this date.</p>
            </div>
          ) : (
            <div className="staff-table-wrapper">
              <table className="staff-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Guests</th>
                    <th>Table</th>
                    <th>Email</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((res) => (
                    <tr key={res.reservation_id}>
                      <td className="staff-table__time">{formatTime(res.time_slot)}</td>
                      <td className="staff-table__name">{res.customer_name}</td>
                      <td>{res.num_guests}</td>
                      <td>#{res.table_number}</td>
                      <td>{res.email}</td>
                      <td>{res.phone_number || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Staff;
